import React, {
    useEffect,
    useState
} from "react";

import Swal from "sweetalert2";

import {
    FaEdit,
    FaTrash,
    FaPlus,
    FaTimes,
    FaFilePdf,
    FaDownload,
    FaImage,
    FaSave,
    FaSyncAlt,
    FaCheckSquare,
    FaSquare
} from "react-icons/fa";

import API_BASE_URL from "../config/api";

import QuillEditor from "../components/QuillEditor";


const Jobs = () => {

    // =========================================================
    // API URL
    // =========================================================

    const API_URL = API_BASE_URL.replace(/\/$/, "");


    // =========================================================
    // STORAGE URL
    // =========================================================

    const STORAGE_BASE_URL =
        API_URL.replace(/\/api$/, "");

    const STORAGE_URL =
        `${STORAGE_BASE_URL}/storage/jobs`;


    // =========================================================
    // STATES
    // =========================================================

    const [jobs, setJobs] = useState([]);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [showForm, setShowForm] = useState(false);

    const [editingId, setEditingId] = useState(null);

    const [selectedIds, setSelectedIds] = useState([]);

    const [selectAll, setSelectAll] = useState(false);


    // =========================================================
    // PAGINATION
    // =========================================================

    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        from: 0,
        to: 0
    });


    // =========================================================
    // FORM DATA
    // =========================================================

    const [formData, setFormData] = useState({

        title: "",

        author: "",

        description: "",

        image: null,

        pdf: null

    });


    // =========================================================
    // PREVIEWS
    // =========================================================

    const [imagePreview, setImagePreview] = useState("");

    const [existingPdf, setExistingPdf] = useState("");


    // =========================================================
    // LOAD JOBS
    // =========================================================

    useEffect(() => {

        fetchJobs(1);

    }, []);


    // =========================================================
    // FETCH JOBS
    // =========================================================

    const fetchJobs = async (page = 1) => {

        try {

            setLoading(true);

            const response = await fetch(
                `${API_URL}/admin/jobs?page=${page}`,
                {
                    method: "GET",

                    credentials: "include",

                    headers: {
                        Accept: "application/json"
                    }
                }
            );


            const result =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    result.message ||
                    "Failed to fetch jobs"
                );

            }


            setJobs(
                result.data || []
            );


            setPagination(
                result.pagination || {
                    current_page: page,
                    last_page: 1,
                    per_page: 15,
                    total: 0,
                    from: 0,
                    to: 0
                }
            );


            setSelectedIds([]);

            setSelectAll(false);


        } catch (error) {

            console.error(
                "Fetch jobs error:",
                error
            );


            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.message ||
                    "Unable to fetch jobs"
            });


        } finally {

            setLoading(false);

        }

    };


    // =========================================================
    // INPUT CHANGE
    // =========================================================

    const handleInputChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setFormData((prev) => ({

            ...prev,

            [name]: value

        }));

    };


    // =========================================================
    // QUILL DESCRIPTION CHANGE
    // =========================================================

    const handleDescriptionChange = (value) => {

        setFormData((prev) => ({

            ...prev,

            description: value

        }));

    };


    // =========================================================
    // IMAGE CHANGE
    // =========================================================

    const handleImageChange = (e) => {

        const file =
            e.target.files?.[0];


        if (!file) {

            return;

        }


        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/webp"
        ];


        if (!allowedTypes.includes(file.type)) {

            Swal.fire({
                icon: "warning",
                title: "Invalid Image",
                text:
                    "Please select JPG, PNG or WEBP image."
            });


            e.target.value = "";

            return;

        }


        setFormData((prev) => ({

            ...prev,

            image: file

        }));


        setImagePreview(
            URL.createObjectURL(file)
        );

    };


    // =========================================================
    // PDF CHANGE
    // =========================================================

    const handlePdfChange = (e) => {

        const file =
            e.target.files?.[0];


        if (!file) {

            return;

        }


        if (
            file.type !==
            "application/pdf"
        ) {

            Swal.fire({
                icon: "warning",
                title: "Invalid File",
                text:
                    "Please select a PDF file."
            });


            e.target.value = "";

            return;

        }


        setFormData((prev) => ({

            ...prev,

            pdf: file

        }));

    };


    // =========================================================
    // RESET FORM
    // =========================================================

    const resetForm = () => {

        setFormData({

            title: "",

            author: "",

            description: "",

            image: null,

            pdf: null

        });


        setImagePreview("");

        setExistingPdf("");

        setEditingId(null);

        setShowForm(false);

    };


    // =========================================================
    // ADD JOB
    // =========================================================

    const handleAdd = () => {

        setFormData({

            title: "",

            author: "",

            description: "",

            image: null,

            pdf: null

        });


        setImagePreview("");

        setExistingPdf("");

        setEditingId(null);

        setShowForm(true);

    };


    // =========================================================
    // EDIT JOB
    // =========================================================

    const handleEdit = async (id) => {

        try {

            setLoading(true);


            const response = await fetch(
                `${API_URL}/admin/jobs/${id}`,
                {
                    method: "GET",

                    credentials: "include",

                    headers: {
                        Accept: "application/json"
                    }
                }
            );


            const result =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    result.message ||
                    "Failed to fetch job"
                );

            }


            const job =
                result.data;


            setEditingId(
                job.job_id
            );


            setFormData({

                title:
                    job.title || "",

                author:
                    job.author || "",

                description:
                    job.description || "",

                image: null,

                pdf: null

            });


            // Existing Image
            if (job.image) {

                setImagePreview(
                    `${STORAGE_URL}/${job.image}`
                );

            } else {

                setImagePreview("");

            }


            // Existing PDF
            if (job.download) {

                setExistingPdf(
                    `${STORAGE_URL}/${job.download}`
                );

            } else {

                setExistingPdf("");

            }


            setShowForm(true);


        } catch (error) {

            console.error(
                "Edit job error:",
                error
            );


            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.message ||
                    "Unable to load job"
            });


        } finally {

            setLoading(false);

        }

    };


    // =========================================================
    // SAVE / UPDATE JOB
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();


        // =====================================================
        // TITLE VALIDATION
        // =====================================================

        if (!formData.title.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Title Required",
                text:
                    "Please enter job title."
            });

            return;

        }


        // =====================================================
        // AUTHOR VALIDATION
        // =====================================================

        if (!formData.author.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Author Required",
                text:
                    "Please enter author name."
            });

            return;

        }


        // =====================================================
        // DESCRIPTION VALIDATION
        // =====================================================

        const plainDescription =
            formData.description
                .replace(/<(.|\n)*?>/g, "")
                .replace(/&nbsp;/g, " ")
                .trim();


        if (!plainDescription) {

            Swal.fire({
                icon: "warning",
                title: "Description Required",
                text:
                    "Please enter job description."
            });

            return;

        }


        // =====================================================
        // IMAGE VALIDATION
        // =====================================================

        if (
            !editingId &&
            !formData.image
        ) {

            Swal.fire({
                icon: "warning",
                title: "Image Required",
                text:
                    "Please select job image."
            });

            return;

        }


        try {

            setSaving(true);


            // =================================================
            // FORM DATA
            // =================================================

            const data =
                new FormData();


            // =================================================
            // TEXT
            // =================================================

            data.append(
                "title",
                formData.title.trim()
            );


            data.append(
                "author",
                formData.author.trim()
            );


            data.append(
                "description",
                formData.description
            );


            // =================================================
            // IMAGE
            // =================================================

            if (formData.image) {

                data.append(
                    "image",
                    formData.image
                );

            }


            // =================================================
            // PDF
            // =================================================

            if (formData.pdf) {

                data.append(
                    "pdf",
                    formData.pdf
                );

            }


            // =================================================
            // URL
            // =================================================

            let url =
                `${API_URL}/admin/jobs`;


            /*
            |--------------------------------------------------------------------------
            | ADD
            |--------------------------------------------------------------------------
            */

            if (!editingId) {

                url =
                    `${API_URL}/admin/jobs`;

            }


            /*
            |--------------------------------------------------------------------------
            | UPDATE
            |--------------------------------------------------------------------------
            |
            | IMPORTANT:
            | Laravel route is POST /admin/jobs/{id}
            |
            | So we DO NOT use:
            | _method=PUT
            |
            | We simply send POST.
            |
            |--------------------------------------------------------------------------
            */

            if (editingId) {

                url =
                    `${API_URL}/admin/jobs/${editingId}`;

            }


            // =================================================
            // REQUEST
            // =================================================

            const response =
                await fetch(
                    url,
                    {

                        method: "POST",

                        credentials: "include",

                        headers: {

                            Accept:
                                "application/json"

                        },

                        body: data

                    }
                );


            const result =
                await response.json();


            // =================================================
            // API ERROR
            // =================================================

            if (!response.ok) {

                console.error(
                    "Job API Error:",
                    result
                );


                // Laravel validation errors
                if (result.errors) {

                    const errors =
                        Object.values(
                            result.errors
                        )
                            .flat()
                            .join("\n");


                    throw new Error(
                        errors
                    );

                }


                throw new Error(
                    result.message ||
                    "Failed to save job"
                );

            }


            // =================================================
            // SUCCESS
            // =================================================

            await Swal.fire({

                icon: "success",

                title: "Success",

                text:
                    result.message ||
                    (
                        editingId
                            ? "Job updated successfully"
                            : "Job added successfully"
                    ),

                timer: 1800,

                showConfirmButton: false

            });


            // =================================================
            // RESET
            // =================================================

            const currentPage =
                pagination.current_page;


            resetForm();


            // =================================================
            // REFRESH
            // =================================================

            fetchJobs(
                currentPage
            );


        } catch (error) {

            console.error(
                "Save job error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Error",

                text:
                    error.message ||
                    "Unable to save job"

            });


        } finally {

            setSaving(false);

        }

    };


    // =========================================================
    // SINGLE DELETE
    // =========================================================

    const handleDelete = async (id) => {

        const confirm =
            await Swal.fire({

                icon: "warning",

                title: "Delete Job?",

                text:
                    "This job and its files will be permanently deleted.",

                showCancelButton: true,

                confirmButtonText:
                    "Yes, Delete",

                cancelButtonText:
                    "Cancel"

            });


        if (!confirm.isConfirmed) {

            return;

        }


        try {

            setLoading(true);


            const response =
                await fetch(
                    `${API_URL}/admin/jobs/${id}`,
                    {

                        method: "DELETE",

                        credentials: "include",

                        headers: {

                            Accept:
                                "application/json"

                        }

                    }
                );


            const result =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    result.message ||
                    "Failed to delete job"
                );

            }


            await Swal.fire({

                icon: "success",

                title: "Deleted",

                text:
                    result.message ||
                    "Job deleted successfully",

                timer: 1500,

                showConfirmButton: false

            });


            fetchJobs(
                pagination.current_page
            );


        } catch (error) {

            console.error(
                "Delete job error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Error",

                text:
                    error.message ||
                    "Unable to delete job"

            });


        } finally {

            setLoading(false);

        }

    };


    // =========================================================
    // SELECT SINGLE
    // =========================================================

    const handleSelect = (id) => {

        setSelectedIds((prev) => {

            if (
                prev.includes(id)
            ) {

                const updated =
                    prev.filter(
                        (item) =>
                            item !== id
                    );


                setSelectAll(
                    updated.length ===
                    jobs.length &&
                    jobs.length > 0
                );


                return updated;

            }


            const updated = [
                ...prev,
                id
            ];


            setSelectAll(
                updated.length ===
                jobs.length &&
                jobs.length > 0
            );


            return updated;

        });

    };


    // =========================================================
    // SELECT ALL
    // =========================================================

    const handleSelectAll = () => {

        if (selectAll) {

            setSelectedIds([]);

            setSelectAll(false);

            return;

        }


        const ids =
            jobs.map(
                (job) =>
                    job.job_id
            );


        setSelectedIds(ids);

        setSelectAll(
            ids.length > 0
        );

    };


    // =========================================================
    // MULTIPLE DELETE
    // =========================================================

    const handleDeleteMultiple = async () => {

        if (
            selectedIds.length === 0
        ) {

            Swal.fire({

                icon: "warning",

                title: "No Jobs Selected",

                text:
                    "Please select at least one job."

            });

            return;

        }


        const confirm =
            await Swal.fire({

                icon: "warning",

                title:
                    "Delete Selected Jobs?",

                text:
                    `${selectedIds.length} job(s) will be permanently deleted.`,

                showCancelButton: true,

                confirmButtonText:
                    "Yes, Delete",

                cancelButtonText:
                    "Cancel"

            });


        if (!confirm.isConfirmed) {

            return;

        }


        try {

            setLoading(true);


            const response =
                await fetch(
                    `${API_URL}/admin/jobs/delete-multiple`,
                    {

                        method: "POST",

                        credentials: "include",

                        headers: {

                            Accept:
                                "application/json",

                            "Content-Type":
                                "application/json"

                        },

                        body:
                            JSON.stringify({
                                ids:
                                    selectedIds
                            })

                    }
                );


            const result =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    result.message ||
                    "Failed to delete selected jobs"
                );

            }


            await Swal.fire({

                icon: "success",

                title: "Deleted",

                text:
                    result.message ||
                    "Selected jobs deleted successfully",

                timer: 1500,

                showConfirmButton: false

            });


            setSelectedIds([]);

            setSelectAll(false);


            fetchJobs(
                pagination.current_page
            );


        } catch (error) {

            console.error(
                "Multiple delete error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Error",

                text:
                    error.message ||
                    "Unable to delete selected jobs"

            });


        } finally {

            setLoading(false);

        }

    };


    // =========================================================
    // PAGE CHANGE
    // =========================================================

    const handlePageChange = (page) => {

        if (
            page < 1 ||
            page >
                pagination.last_page
        ) {

            return;

        }


        fetchJobs(page);

    };


    // =========================================================
    // FORMAT DATE
    // =========================================================

    const formatDate = (date) => {

        if (!date) {

            return "-";

        }


        const d =
            new Date(date);


        if (
            Number.isNaN(
                d.getTime()
            )
        ) {

            return date;

        }


        return d.toLocaleDateString(
            "en-US",
            {
                month: "short",
                day: "2-digit",
                year: "numeric"
            }
        );

    };


    // =========================================================
    // PAGE NUMBERS
    // =========================================================

    const getPageNumbers = () => {

        const pages = [];


        const current =
            pagination.current_page;


        const last =
            pagination.last_page;


        let start =
            Math.max(
                1,
                current - 2
            );


        let end =
            Math.min(
                last,
                current + 2
            );


        if (
            end - start < 4
        ) {

            if (start === 1) {

                end =
                    Math.min(
                        last,
                        5
                    );

            } else {

                start =
                    Math.max(
                        1,
                        last - 4
                    );

            }

        }


        for (
            let i = start;
            i <= end;
            i++
        ) {

            pages.push(i);

        }


        return pages;

    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            className="container-fluid"
            style={{
                maxHeight: "85vh",
                overflowY: "auto"
            }}
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <div
                className="d-flex justify-content-between align-items-center mb-4"
                style={{
                    gap: "10px",
                    flexWrap: "wrap"
                }}
            >

                <div>

                    <h3
                        style={{
                            margin: 0,
                            fontWeight: 600
                        }}
                    >
                        Jobs & Current Affairs
                    </h3>

                    <small
                        className="text-muted"
                    >
                        Manage jobs and current affairs
                    </small>

                </div>


                {!showForm && (

                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap"
                        }}
                    >

                        {/* ADD */}

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleAdd}
                        >

                            <FaPlus
                                style={{
                                    marginRight: "6px"
                                }}
                            />

                            Add Job

                        </button>


                        {/* REFRESH */}

                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() =>
                                fetchJobs(
                                    pagination.current_page
                                )
                            }
                        >

                            <FaSyncAlt
                                style={{
                                    marginRight: "6px"
                                }}
                            />

                            Refresh

                        </button>


                        {/* DELETE SELECTED */}

                        {selectedIds.length > 0 && (

                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={
                                    handleDeleteMultiple
                                }
                            >

                                <FaTrash
                                    style={{
                                        marginRight: "6px"
                                    }}
                                />

                                Delete Selected (
                                {selectedIds.length}
                                )

                            </button>

                        )}

                    </div>

                )}

            </div>


            {/* =================================================
                FORM
            ================================================= */}

            {showForm ? (

                <div
                    className="card"
                    style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        marginBottom: "20px"
                    }}
                >

                    {/* FORM HEADER */}

                    <div
                        className="card-header"
                        style={{
                            padding: "15px 20px",
                            borderBottom:
                                "1px solid #ddd",
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center"
                        }}
                    >

                        <h5
                            style={{
                                margin: 0,
                                fontWeight: 600
                            }}
                        >
                            {editingId
                                ? "Edit Job"
                                : "Add New Job"}
                        </h5>


                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={resetForm}
                        >

                            <FaTimes />

                        </button>

                    </div>


                    {/* FORM BODY */}

                    <div
                        style={{
                            padding: "20px"
                        }}
                    >

                        <form
                            onSubmit={handleSubmit}
                        >

                            <div className="row">

                                {/* =================================================
                                    TITLE
                                ================================================= */}

                                <div className="col-md-8 mb-3">

                                    <label
                                        className="form-label"
                                    >

                                        Job Title

                                        <span
                                            style={{
                                                color: "red"
                                            }}
                                        >
                                            {" "}*
                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        name="title"
                                        value={
                                            formData.title
                                        }
                                        onChange={
                                            handleInputChange
                                        }
                                        className="form-control"
                                        maxLength={200}
                                        placeholder="Enter job title"
                                    />

                                </div>


                                {/* =================================================
                                    AUTHOR
                                ================================================= */}

                                <div className="col-md-4 mb-3">

                                    <label
                                        className="form-label"
                                    >

                                        Author

                                        <span
                                            style={{
                                                color: "red"
                                            }}
                                        >
                                            {" "}*
                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        name="author"
                                        value={
                                            formData.author
                                        }
                                        onChange={
                                            handleInputChange
                                        }
                                        className="form-control"
                                        maxLength={100}
                                        placeholder="Enter author name"
                                    />

                                </div>


                                {/* =================================================
                                    DESCRIPTION
                                ================================================= */}

                                <div className="col-md-12 mb-3">

                                    <label
                                        className="form-label"
                                    >

                                        Description

                                        <span
                                            style={{
                                                color: "red"
                                            }}
                                        >
                                            {" "}*
                                        </span>

                                    </label>


                                    <QuillEditor
                                        value={
                                            formData.description
                                        }
                                        onChange={
                                            handleDescriptionChange
                                        }
                                    />


                                    <small
                                        className="text-muted"
                                        style={{
                                            display: "block",
                                            marginTop: "8px"
                                        }}
                                    >
                                        Enter job/current affairs
                                        description using the
                                        rich text editor.
                                    </small>

                                </div>


                                {/* =================================================
                                    IMAGE
                                ================================================= */}

                                <div className="col-md-6 mb-3">

                                    <label
                                        className="form-label"
                                    >

                                        Job Image

                                        {!editingId && (

                                            <span
                                                style={{
                                                    color: "red"
                                                }}
                                            >
                                                {" "}*
                                            </span>

                                        )}

                                    </label>


                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={
                                            handleImageChange
                                        }
                                    />


                                    <small
                                        className="text-muted"
                                    >
                                        JPG, PNG or WEBP
                                    </small>


                                    {imagePreview && (

                                        <div
                                            style={{
                                                marginTop:
                                                    "15px"
                                            }}
                                        >

                                            <img
                                                src={
                                                    imagePreview
                                                }
                                                alt="Job Preview"
                                                style={{
                                                    width:
                                                        "200px",
                                                    height:
                                                        "130px",
                                                    objectFit:
                                                        "cover",
                                                    border:
                                                        "1px solid #ddd",
                                                    borderRadius:
                                                        "6px",
                                                    padding:
                                                        "3px"
                                                }}
                                            />

                                        </div>

                                    )}

                                </div>


                                {/* =================================================
                                    PDF
                                ================================================= */}

                                <div className="col-md-6 mb-3">

                                    <label
                                        className="form-label"
                                    >

                                        PDF / Download Content

                                    </label>


                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="application/pdf"
                                        onChange={
                                            handlePdfChange
                                        }
                                    />


                                    <small
                                        className="text-muted"
                                    >
                                        PDF file only
                                    </small>


                                    {existingPdf && (

                                        <div
                                            style={{
                                                marginTop:
                                                    "15px"
                                            }}
                                        >

                                            <a
                                                href={
                                                    existingPdf
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-outline-danger btn-sm"
                                            >

                                                <FaFilePdf
                                                    style={{
                                                        marginRight:
                                                            "6px"
                                                    }}
                                                />

                                                Existing PDF

                                                <FaDownload
                                                    style={{
                                                        marginLeft:
                                                            "6px"
                                                    }}
                                                />

                                            </a>

                                        </div>

                                    )}

                                </div>


                                {/* =================================================
                                    BUTTONS
                                ================================================= */}

                                <div
                                    className="col-md-12"
                                    style={{
                                        marginTop:
                                            "10px"
                                    }}
                                >

                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        disabled={saving}
                                    >

                                        {saving ? (

                                            <>

                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    style={{
                                                        marginRight:
                                                            "6px"
                                                    }}
                                                />

                                                Saving...

                                            </>

                                        ) : (

                                            <>

                                                <FaSave
                                                    style={{
                                                        marginRight:
                                                            "6px"
                                                    }}
                                                />

                                                {editingId
                                                    ? "Update Job"
                                                    : "Save Job"}

                                            </>

                                        )}

                                    </button>


                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        style={{
                                            marginLeft:
                                                "8px"
                                        }}
                                        onClick={
                                            resetForm
                                        }
                                    >

                                        <FaTimes
                                            style={{
                                                marginRight:
                                                    "6px"
                                            }}
                                        />

                                        Cancel

                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>

                </div>

            ) : (

                /* =================================================
                   JOB LIST
                ================================================= */

                <div
                    className="card"
                    style={{
                        border:
                            "1px solid #ddd",
                        borderRadius:
                            "8px"
                    }}
                >

                    <div
                        style={{
                            overflowX:
                                "auto"
                        }}
                    >

                        <table
                            className="table table-hover"
                            style={{
                                marginBottom: 0,
                                minWidth:
                                    "1000px"
                            }}
                        >

                            <thead>

                                <tr>

                                    {/* SELECT ALL */}

                                    <th
                                        style={{
                                            width:
                                                "50px",
                                            textAlign:
                                                "center"
                                        }}
                                    >

                                        <button
                                            type="button"
                                            onClick={
                                                handleSelectAll
                                            }
                                            style={{
                                                border:
                                                    "none",
                                                background:
                                                    "transparent",
                                                padding:
                                                    "0",
                                                cursor:
                                                    "pointer"
                                            }}
                                        >

                                            {selectAll ? (

                                                <FaCheckSquare
                                                    size={18}
                                                />

                                            ) : (

                                                <FaSquare
                                                    size={18}
                                                />

                                            )}

                                        </button>

                                    </th>


                                    {/* IMAGE */}

                                    <th
                                        style={{
                                            width:
                                                "180px"
                                        }}
                                    >
                                        Image
                                    </th>


                                    {/* TITLE */}

                                    <th>
                                        Title
                                    </th>


                                    {/* AUTHOR */}

                                    <th>
                                        Author
                                    </th>


                                    {/* DATE */}

                                    <th>
                                        Date Added
                                    </th>


                                    {/* DOWNLOAD */}

                                    <th
                                        style={{
                                            width:
                                                "160px"
                                        }}
                                    >
                                        Download
                                    </th>


                                    {/* ACTION */}

                                    <th
                                        style={{
                                            width:
                                                "130px"
                                        }}
                                    >
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {loading ? (

                                    <tr>

                                        <td
                                            colSpan={7}
                                            style={{
                                                textAlign:
                                                    "center",
                                                padding:
                                                    "40px"
                                            }}
                                        >

                                            <div
                                                className="spinner-border"
                                                role="status"
                                            />

                                            <div
                                                style={{
                                                    marginTop:
                                                        "10px"
                                                }}
                                            >
                                                Loading...
                                            </div>

                                        </td>

                                    </tr>

                                ) : jobs.length === 0 ? (

                                    <tr>

                                        <td
                                            colSpan={7}
                                            style={{
                                                textAlign:
                                                    "center",
                                                padding:
                                                    "40px"
                                            }}
                                        >

                                            No jobs found.

                                        </td>

                                    </tr>

                                ) : (

                                    jobs.map(
                                        (job) => (

                                            <tr
                                                key={
                                                    job.job_id
                                                }
                                            >

                                                {/* CHECKBOX */}

                                                <td
                                                    style={{
                                                        textAlign:
                                                            "center"
                                                    }}
                                                >

                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            selectedIds.includes(
                                                                job.job_id
                                                            )
                                                        }
                                                        onChange={() =>
                                                            handleSelect(
                                                                job.job_id
                                                            )
                                                        }
                                                    />

                                                </td>


                                                {/* IMAGE */}

                                                <td>

                                                    {job.image ? (

                                                        <img
                                                            src={
                                                                `${STORAGE_URL}/${job.image}`
                                                            }
                                                            alt={
                                                                job.title
                                                            }
                                                            style={{
                                                                width:
                                                                    "150px",
                                                                height:
                                                                    "95px",
                                                                objectFit:
                                                                    "cover",
                                                                borderRadius:
                                                                    "5px",
                                                                border:
                                                                    "1px solid #ddd"
                                                            }}
                                                            onError={(
                                                                e
                                                            ) => {

                                                                e.currentTarget.style.display =
                                                                    "none";

                                                            }}
                                                        />

                                                    ) : (

                                                        <div
                                                            style={{
                                                                width:
                                                                    "150px",
                                                                height:
                                                                    "95px",
                                                                border:
                                                                    "1px solid #ddd",
                                                                display:
                                                                    "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                color:
                                                                    "#999"
                                                            }}
                                                        >

                                                            <FaImage
                                                                size={
                                                                    25
                                                                }
                                                            />

                                                        </div>

                                                    )}

                                                </td>


                                                {/* TITLE */}

                                                <td>

                                                    <div
                                                        style={{
                                                            fontWeight:
                                                                600
                                                        }}
                                                    >

                                                        {
                                                            job.title
                                                        }

                                                    </div>


                                                    {job.description && (

                                                        <div
                                                            style={{
                                                                marginTop:
                                                                    "6px",
                                                                fontSize:
                                                                    "13px",
                                                                color:
                                                                    "#777",
                                                                maxWidth:
                                                                    "350px",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap"
                                                            }}
                                                        >

                                                            {job.description
                                                                .replace(
                                                                    /<[^>]*>/g,
                                                                    ""
                                                                )
                                                                .replace(
                                                                    /&nbsp;/g,
                                                                    " "
                                                                )
                                                            }

                                                        </div>

                                                    )}

                                                </td>


                                                {/* AUTHOR */}

                                                <td>

                                                    {
                                                        job.author ||
                                                        "-"
                                                    }

                                                </td>


                                                {/* DATE */}

                                                <td>

                                                    {
                                                        formatDate(
                                                            job.date_added
                                                        )
                                                    }

                                                </td>


                                                {/* DOWNLOAD */}

                                                <td>

                                                    {job.download ? (

                                                        <a
                                                            href={
                                                                `${STORAGE_URL}/${job.download}`
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-outline-danger btn-sm"
                                                        >

                                                            <FaFilePdf
                                                                style={{
                                                                    marginRight:
                                                                        "5px"
                                                                }}
                                                            />

                                                            PDF

                                                            <FaDownload
                                                                style={{
                                                                    marginLeft:
                                                                        "5px"
                                                                }}
                                                            />

                                                        </a>

                                                    ) : (

                                                        <span
                                                            style={{
                                                                color:
                                                                    "#999"
                                                            }}
                                                        >

                                                            No PDF

                                                        </span>

                                                    )}

                                                </td>


                                                {/* ACTION */}

                                                <td>

                                                    <div
                                                        style={{
                                                            display:
                                                                "flex",
                                                            gap:
                                                                "6px"
                                                        }}
                                                    >

                                                        {/* EDIT */}

                                                        <button
                                                            type="button"
                                                            className="btn btn-primary btn-sm"
                                                            title="Edit"
                                                            onClick={() =>
                                                                handleEdit(
                                                                    job.job_id
                                                                )
                                                            }
                                                        >

                                                            <FaEdit />

                                                        </button>


                                                        {/* DELETE */}

                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            title="Delete"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    job.job_id
                                                                )
                                                            }
                                                        >

                                                            <FaTrash />

                                                        </button>

                                                    </div>

                                                </td>

                                            </tr>

                                        )
                                    )

                                )}

                            </tbody>

                        </table>

                    </div>


                    {/* =================================================
                        PAGINATION
                    ================================================= */}

                    {!loading &&
                        jobs.length > 0 &&
                        pagination.last_page > 1 && (

                            <div
                                style={{
                                    padding:
                                        "15px",
                                    borderTop:
                                        "1px solid #ddd",
                                    display:
                                        "flex",
                                    justifyContent:
                                        "space-between",
                                    alignItems:
                                        "center",
                                    flexWrap:
                                        "wrap",
                                    gap:
                                        "10px"
                                }}
                            >

                                {/* PAGINATION INFO */}

                                <div
                                    style={{
                                        fontSize:
                                            "14px",
                                        color:
                                            "#666"
                                    }}
                                >

                                    Showing{" "}

                                    {
                                        pagination.from ||
                                        0
                                    }

                                    {" "}to{" "}

                                    {
                                        pagination.to ||
                                        0
                                    }

                                    {" "}of{" "}

                                    {
                                        pagination.total
                                    }

                                    {" "}jobs

                                </div>


                                {/* PAGINATION */}

                                <nav>

                                    <ul
                                        className="pagination"
                                        style={{
                                            margin:
                                                0
                                        }}
                                    >

                                        {/* PREVIOUS */}

                                        <li
                                            className={
                                                `page-item ${
                                                    pagination.current_page ===
                                                    1
                                                        ? "disabled"
                                                        : ""
                                                }`
                                            }
                                        >

                                            <button
                                                className="page-link"
                                                type="button"
                                                disabled={
                                                    pagination.current_page ===
                                                    1
                                                }
                                                onClick={() =>
                                                    handlePageChange(
                                                        pagination.current_page -
                                                            1
                                                    )
                                                }
                                            >
                                                Previous
                                            </button>

                                        </li>


                                        {/* PAGE NUMBERS */}

                                        {getPageNumbers().map(
                                            (page) => (

                                                <li
                                                    key={
                                                        page
                                                    }
                                                    className={
                                                        `page-item ${
                                                            pagination.current_page ===
                                                            page
                                                                ? "active"
                                                                : ""
                                                        }`
                                                    }
                                                >

                                                    <button
                                                        type="button"
                                                        className="page-link"
                                                        onClick={() =>
                                                            handlePageChange(
                                                                page
                                                            )
                                                        }
                                                    >

                                                        {
                                                            page
                                                        }

                                                    </button>

                                                </li>

                                            )
                                        )}


                                        {/* NEXT */}

                                        <li
                                            className={
                                                `page-item ${
                                                    pagination.current_page ===
                                                    pagination.last_page
                                                        ? "disabled"
                                                        : ""
                                                }`
                                            }
                                        >

                                            <button
                                                className="page-link"
                                                type="button"
                                                disabled={
                                                    pagination.current_page ===
                                                    pagination.last_page
                                                }
                                                onClick={() =>
                                                    handlePageChange(
                                                        pagination.current_page +
                                                            1
                                                    )
                                                }
                                            >
                                                Next
                                            </button>

                                        </li>

                                    </ul>

                                </nav>

                            </div>

                        )}

                </div>

            )}

        </div>

    );

};


export default Jobs;